/* eslint-disable max-lines */
import type { AvatarProps } from '@archipro-design/aria';
import { HotButtons, pxToRem } from '@archipro-design/aria';
import { Grid } from '@archipro-design/aria';
import { PaginationItem } from '@archipro-design/aria';
import {
    Flex,
    Pagination,
    Paragraph,
    useAppDisplayModeContext,
    useStyles,
} from '@archipro-design/aria';
import { Link, useLocation, useSearchParams } from '@remix-run/react';
import * as S from './SearchResultsGrid.style';
import type {
    GroupedSearchResult,
    SearchResultItem,
    SearchResults,
} from '@modules/search/type';
import React, { useState } from 'react';
import { mapESProductTileViewModel } from '@modules/search/util/elasticsearch-product-tile-view';
import { mapESProfessionalTileViewModel } from '@modules/search/util/elasticsearch-professional-tile-view';
import { mapESProjectTileViewModel } from '@modules/search/util/elasticsearch-project-tile-view';
import { mapESArchizeenTileViewModel } from '@modules/search/util/elasticsearch-archizeen-tile-view';
import type { PaginationState } from '@modules/pagination/type';
import { useTracker } from '@archipro-website/tracker';
import { fireSearchTileEvent } from '../../util/search-track-helpers';
import ShoppingAvailable from '~/modules/tile/component/product-tile/ShoppingAvailable';
import { getCategoryFromUrl } from '@archipro-website/top-navigation';
import cx from 'classnames';
import { toTitleCase } from '../../util/stringHelper';
import VariantDot from '~/modules/tile/component/product-tile/VariantDot';
import ArchizeenTile from '~/modules/tile/component/archizeen-tile/ArchizeenImpressionTile';
import type { TileProps as ArchizeenTileProps } from '~/modules/tile/component/archizeen-tile/ArchizeenImpressionTile';
import ProjectTile from '~/modules/tile/component/project-tile/ProjectImpressionTile';
import type { TileProps as ProjectTileProps } from '~/modules/tile/component/project-tile/ProjectImpressionTile';
import ProductTile from '~/modules/tile/component/product-tile/ProductImpressionTile';
import type { TileProps as ProductTileProps } from '~/modules/tile/component/product-tile/ProductImpressionTile';
import ProfessionalTile from '~/modules/tile/component/professional-tile/ProfessionalImpressionTile';
import type { TileProps as ProfessionalTileProps } from '~/modules/tile/component/professional-tile/ProfessionalImpressionTile';
import { trimPaginationURL } from '~/modules/pagination/util/trim-pagination-url';
import { SearchResultCarousels } from './SearchResultCarousels';
import { useRootPermissions, useUser } from '@modules/user';
import { useItemAdminData } from '~/modules/directory/hook/use-item-admin-data';
import { useToggleHomePin } from '~/modules/home/hook/use-toggle-home-pin';
import { getHotButtons } from '~/modules/directory/util/get-hot-buttons';
import MessageModal from '~/modules/home/component/common/MessageModal';
import { visitBMEditor } from '~/modules/directory/util/tile-helpers';
import type { EditableItem } from '~/modules/directory/type';
import { useABTestIsOn } from '@modules/root/hook/use-growthbook';
import { FollowSelectorProxy } from '~/modules/moodboards/components';
import { useHandleFollow } from '~/modules-rocco/design-board/hook/use-handle-follow';
import { useToaster } from '~/modules/root/context/ToasterContext';

interface SearchResultsGridProps {
    searchResults: SearchResults<SearchResultItem> | null;
    pagination?: PaginationState | null;
    filterDrawerOpen?: boolean;
    groupedSearchResult: GroupedSearchResult | null;
}
export const DEFAULT_PAGE_SIZE = 40;

/**
 * @deprecated This was used in the old search page before Rocco.
 */
export const SearchResultsGrid = (props: SearchResultsGridProps) => {
    const tracker = useTracker();
    const { css } = useStyles({
        filterDrawerOpen: props.filterDrawerOpen,
    });
    const user = useUser();
    const location = useLocation();
    const appDisplayMode = useAppDisplayModeContext();
    const { mobile, desktop } = appDisplayMode;
    const { searchResults, pagination, groupedSearchResult } = props;
    const [searchParams] = useSearchParams();
    const { ITEM_TYPE, DIRECTORY } = getCategoryFromUrl(location);
    const { isInDesignBoard } = useHandleFollow();

    const currentCategory = DIRECTORY;
    const currentCategories = toTitleCase(ITEM_TYPE) + 's';
    const { editProfile } = useRootPermissions();

    const itemIDs = searchResults?.hits.map((hit) => hit.id) ?? [];
    const adminTileMap = useItemAdminData(itemIDs);
    const { toast } = useToaster();

    const [pinErrorMessage, setPinErrorMessage] = useState<string | null>(null);
    const { toggleHomePin } = useToggleHomePin(
        (error) => {
            if (error.Message) {
                setPinErrorMessage(error.Message);
            }
        },
        (pinned) => {
            toast(pinned ? 'Pinned to homepage.' : 'Unpinned from homepage.', {
                type: 'success',
            });
        },
    );

    const showDownloads = useABTestIsOn('directory-tile-downloads');

    const columnMap = () => {
        if (mobile) {
            switch (currentCategory) {
                case 'product':
                    return 2;
                case 'project':
                case 'professional':
                case 'article':
                    return 1;
                default:
                    return 2;
            }
        }

        return props.filterDrawerOpen ? 4 : 5;
    };

    const numberOfColumns = columnMap();
    const gridMaxWidth = props.filterDrawerOpen ? 1360 : 1820;
    const columnWidthDesktop = gridMaxWidth / numberOfColumns;
    const gap = 10;

    const desktopGridColumns = (numberOfColumns: number) => {
        return `repeat(${numberOfColumns}, ${pxToRem(columnWidthDesktop)})`;
    };

    const onTileClick = (index: number) => {
        fireSearchTileEvent(
            'SearchTileClick',
            window.location.href,
            {
                index,
                columnPerRow: props.filterDrawerOpen ? 4 : 5,
            },
            tracker,
        );
    };

    const getHotButtonProps = (bmEditorItem: EditableItem) => {
        return {
            onEditClick: () => visitBMEditor(bmEditorItem, false),
            onRepositionClick: () => {
                visitBMEditor(bmEditorItem, false, {
                    repositionThumb: true,
                });
            },
            canEdit: bmEditorItem.canEdit ?? false,
        };
    };

    const makeProductTile = (
        item: SearchResultItem,
        onTileClick?: () => void,
        props?: Partial<ProductTileProps>,
        showDownloads?: boolean,
    ) => {
        const {
            productLink,
            professionalID,
            professionalName,
            canPurchase,
            variants,
            canEdit: _canEdit,
            bmEditorLink: _bmEditorLink,
            repositionThumbLink,
            image,
            downloads,
            ...rest
        } = mapESProductTileViewModel(item, user).tile;
        const adminItem = adminTileMap[item.id];

        const hotButtons = getHotButtons({
            item,
            itemType: 'product',
            appDisplayMode,
            canEditAdmin: editProfile,
            pinned: !!adminItem?.homePinned,
            onPinClick: toggleHomePin
                ? (item) => {
                      if (!adminItem) {
                          return;
                      }
                      toggleHomePin(item.id, !adminItem.homePinned);
                  }
                : undefined,
            ...getHotButtonProps({
                canEdit: _canEdit,
                bmEditorLink: _bmEditorLink,
                repositionThumbLink,
            }),
        });
        const topActions: React.ReactNode[] = [];
        if (hotButtons) {
            topActions.push(
                <HotButtons key="hot-buttons" items={hotButtons} />,
            );
        }

        return (
            <FollowSelectorProxy pinId={item.id} pinType="ProductPin">
                {({ openFollowSelector }) => (
                    <ProductTile
                        key={productLink}
                        as={Link}
                        to={productLink}
                        prefetch={'intent'}
                        image={[
                            {
                                src: image[0]?.src || '',
                                mixBlendMode:
                                    image[0]?.mixBlendMode || 'normal',
                                alt: rest.productName,
                            },
                        ]}
                        {...rest}
                        professionalName={professionalName}
                        onClick={() => {
                            onTileClick?.();
                        }}
                        onFavourite={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            openFollowSelector(e);
                        }}
                        isFavourited={isInDesignBoard(item.id, 'ProductPin')}
                        topContent={
                            canPurchase
                                ? [
                                      <ShoppingAvailable
                                          key="shopping-available"
                                          floating={'left'}
                                      />,
                                  ]
                                : []
                        }
                        variants={variants?.map((v) => ({
                            children: (
                                <VariantDot
                                    {...v}
                                    size={appDisplayMode.mobile ? 9 : 13}
                                    colorCode={v.colorCode ?? ''}
                                />
                            ),
                        }))}
                        variant={desktop ? '01' : '02'}
                        size={desktop ? 'dynamic' : 'medium'}
                        variables={{
                            tilePadding: pxToRem(gap),
                            dynamicSizingWidth: columnWidthDesktop,
                            dynamicSizingGapWidth: gap,
                        }}
                        impression={{
                            professionalID: professionalID,
                            itemID: item.id,
                            type: 'LibraryItem_Directory',
                        }}
                        topActions={topActions}
                        newDesignEnabled={true}
                        downloads={showDownloads ? downloads : undefined}
                        {...props}
                    />
                )}
            </FollowSelectorProxy>
        );
    };

    const makeProfessionalTile = (
        item: SearchResultItem,
        onTileClick?: () => void,
        props?: Partial<ProfessionalTileProps>,
    ) => {
        const viewModal = mapESProfessionalTileViewModel(
            item,
            'small',
            mobile,
            user,
        );

        const { link } = viewModal;
        const {
            canEdit: _canEdit,
            bmEditorLink: _bmEditorLink,
            repositionThumbLink,
            ...tile
        } = viewModal.tile;

        const adminItem = adminTileMap[item.id];

        const hotButtons = getHotButtons({
            item,
            itemType: 'professional',
            appDisplayMode,
            canEditAdmin: editProfile,
            pinned: !!adminItem?.homePinned,
            onPinClick: toggleHomePin
                ? (item) => {
                      if (!adminItem) {
                          return;
                      }
                      toggleHomePin(item.id, !adminItem.homePinned);
                  }
                : undefined,
            ...getHotButtonProps({
                canEdit: _canEdit,
                bmEditorLink: _bmEditorLink,
                repositionThumbLink,
            }),
        });
        const topActions: React.ReactNode[] = [];
        if (hotButtons) {
            topActions.push(
                <HotButtons key="hot-buttons" items={hotButtons} />,
            );
        }

        return (
            <FollowSelectorProxy pinId={item.id} pinType="ProfessionalPin">
                {({ openFollowSelector }) => (
                    <ProfessionalTile
                        key={link}
                        className={currentCategory}
                        as={Link}
                        prefetch="intent"
                        to={link}
                        {...tile}
                        image={[
                            {
                                src:
                                    typeof tile.image === 'object'
                                        ? (tile.image[0] ?? '')
                                        : '',
                                alt:
                                    typeof tile.title === 'string'
                                        ? tile.title
                                        : '',
                                loading: 'lazy',
                            },
                        ]}
                        logo={{
                            image: {
                                src:
                                    typeof tile.logo === 'object'
                                        ? (tile.logo.image as string)
                                        : tile.logo,
                                alt: `${tile.title} professional logo`,
                            },
                            ...(typeof tile.logo === 'object' && {
                                variables: (tile.logo as unknown as AvatarProps)
                                    .variables,
                            }),
                        }}
                        onClick={() => {
                            onTileClick?.();
                        }}
                        onFavourite={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            openFollowSelector(e);
                        }}
                        isFavourited={isInDesignBoard(
                            item.id,
                            'ProfessionalPin',
                        )}
                        size={!desktop ? 'small' : 'dynamic'}
                        variant={'04'}
                        variables={{
                            tilePadding: pxToRem(gap),
                            dynamicSizingWidth: columnWidthDesktop,
                            dynamicSizingGapWidth: gap,
                            avatorYMargin: 20,

                            ...(!desktop && {
                                dynamicSizingWidth: 414,
                                dynamicSizingGapWidth: 0,
                                tilePadding: 0,
                                avatarMargin: 18,
                            }),
                        }}
                        impression={{
                            professionalID: item.id,
                            itemID: item.id,
                            type: 'LibraryItem_Directory',
                        }}
                        topActions={topActions}
                        tagsToShowInFull={1}
                        {...props}
                    />
                )}
            </FollowSelectorProxy>
        );
    };

    const makeProjectTile = (
        item: SearchResultItem,
        onTileClick?: () => void,
        props?: Partial<ProjectTileProps>,
    ) => {
        const {
            projectLink,
            professionalID,
            professionalName,
            projectID,
            canEdit: _canEdit,
            bmEditorLink: _bmEditorLink,
            repositionThumbLink,
            ...rest
        } = mapESProjectTileViewModel(item, 'small', mobile, user).tile;

        const adminItem = adminTileMap[item.id];

        const hotButtons = getHotButtons({
            item,
            itemType: 'project',
            appDisplayMode,
            canEditAdmin: editProfile,
            pinned: !!adminItem?.homePinned,
            onPinClick: toggleHomePin
                ? (item) => {
                      if (!adminItem) {
                          return;
                      }
                      toggleHomePin(item.id, !adminItem.homePinned);
                  }
                : undefined,
            ...getHotButtonProps({
                canEdit: _canEdit,
                bmEditorLink: _bmEditorLink,
                repositionThumbLink,
            }),
        });
        const topActions: React.ReactNode[] = [];
        if (hotButtons) {
            topActions.push(
                <HotButtons key="hot-buttons" items={hotButtons} />,
            );
        }

        return (
            <FollowSelectorProxy pinId={item.id} pinType="ProjectPin">
                {({ openFollowSelector }) => (
                    <ProjectTile
                        key={projectID}
                        as={Link}
                        to={projectLink}
                        prefetch="intent"
                        professionalName={professionalName}
                        {...rest}
                        image={[
                            {
                                src: rest.image[0] ?? '',
                                alt: rest.projectName,
                            },
                        ]}
                        onClick={() => {
                            onTileClick?.();
                        }}
                        onFavourite={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            openFollowSelector(e);
                        }}
                        isFavourited={isInDesignBoard(item.id, 'ProjectPin')}
                        size={'dynamic'}
                        variant={appDisplayMode.desktop ? '01' : '02'}
                        variables={{
                            tilePadding: pxToRem(gap),
                            dynamicSizingWidth: columnWidthDesktop,
                            dynamicSizingGapWidth: gap,

                            ...(!desktop && {
                                dynamicSizingWidth: 414,
                                dynamicSizingGapWidth: 0,
                                tilePadding: 0,
                            }),
                        }}
                        impression={{
                            professionalID: professionalID,
                            itemID: projectID,
                            type: 'LibraryItem_Directory',
                        }}
                        topActions={topActions}
                        newDesignEnabled={true}
                        savedCount={undefined}
                        viewedCount={undefined}
                        {...props}
                    />
                )}
            </FollowSelectorProxy>
        );
    };

    const makeArticleTile = (
        item: SearchResultItem,
        onTileClick?: () => void,
        props?: Partial<ArchizeenTileProps>,
        isPortrait?: boolean,
    ) => {
        const { link, tile } = mapESArchizeenTileViewModel(
            item,
            'medium',
            user,
        );

        const {
            canEdit: _canEdit,
            bmEditorLink: _bmEditorLink,
            ID: _,
            repositionThumbLink,
            ...rest
        } = tile;

        const adminItem = adminTileMap[item.id];

        const hotButtons = getHotButtons({
            item,
            itemType: 'article',
            appDisplayMode,
            canEditAdmin: editProfile,
            pinned: !!adminItem?.homePinned,
            onPinClick: toggleHomePin
                ? (item) => {
                      if (!adminItem) {
                          return;
                      }
                      toggleHomePin(item.id, !adminItem.homePinned);
                  }
                : undefined,
            ...getHotButtonProps({
                canEdit: _canEdit,
                bmEditorLink: _bmEditorLink,
                repositionThumbLink,
            }),
        });
        const topActions: React.ReactNode[] = [];
        if (hotButtons) {
            topActions.push(
                <HotButtons key="hot-buttons" items={hotButtons} />,
            );
        }

        return (
            <FollowSelectorProxy pinId={item.id} pinType="ArticlePin">
                {({ openFollowSelector }) => (
                    <>
                        {appDisplayMode.desktop ? (
                            <ArchizeenTile
                                key={link}
                                as={Link}
                                to={link}
                                prefetch="intent"
                                {...rest}
                                image={{
                                    src:
                                        typeof tile.image === 'string'
                                            ? tile.image
                                            : '',
                                    alt:
                                        typeof tile.title === 'string'
                                            ? tile.title
                                            : '',
                                }}
                                variant={isPortrait ? '02' : '01'}
                                size={'dynamic'}
                                variables={{
                                    dynamicTitleGap: pxToRem(32),
                                    tilePadding: pxToRem(gap),
                                    dynamicSizingWidth: columnWidthDesktop,
                                    dynamicSizingGapWidth: gap,
                                }}
                                onFavourite={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    openFollowSelector(e);
                                }}
                                isFavourited={isInDesignBoard(
                                    item.id,
                                    'ArticlePin',
                                )}
                                onClick={() => {
                                    onTileClick?.();
                                }}
                                impression={{
                                    professionalID: item.ProfessionalID ?? 0,
                                    itemID: item.id,
                                    type: 'LibraryItem_Directory',
                                }}
                                topActions={topActions}
                                {...props}
                            />
                        ) : (
                            <ArchizeenTile
                                key={link}
                                className={cx(
                                    css(S.ArticleMobileStyle),
                                    !currentCategory
                                        ? 'all'
                                        : isPortrait
                                          ? 'portrait'
                                          : 'landscape',
                                )}
                                as={Link}
                                to={link}
                                prefetch="intent"
                                {...rest}
                                variant={'04'}
                                title={{
                                    children: item.Title,
                                    maximumLines: 3,
                                }}
                                size={isPortrait ? 'medium' : 'small'}
                                favouritePosition={'card'}
                                onFavourite={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    openFollowSelector(e);
                                }}
                                isFavourited={isInDesignBoard(
                                    item.id,
                                    'ArticlePin',
                                )}
                                design={{
                                    paddingBottom: pxToRem(22),
                                }}
                                onClick={() => {
                                    onTileClick?.();
                                }}
                                image={{
                                    src:
                                        typeof tile.image === 'string'
                                            ? tile.image
                                            : '',
                                    alt:
                                        typeof tile.title === 'string'
                                            ? tile.title
                                            : '',
                                }}
                                impression={{
                                    professionalID: item.ProfessionalID ?? 0,
                                    itemID: item.id,
                                    type: 'LibraryItem_Directory',
                                }}
                                {...props}
                            />
                        )}
                    </>
                )}
            </FollowSelectorProxy>
        );
    };

    const tiles =
        (searchResults?.hits?.map((item, i) => {
            if (item.PType === 'Product') {
                return makeProductTile(
                    item,
                    () => onTileClick(i),
                    undefined,
                    showDownloads,
                );
            }
            if (item.PType === 'Professional') {
                return makeProfessionalTile(item, () => onTileClick(i));
            }
            if (item.PType === 'Project') {
                return makeProjectTile(item, () => onTileClick(i));
            }
            if (item.PType === 'Blog') {
                const isAlternateRow =
                    Math.floor(i / numberOfColumns) % 2 === 0;
                const oddOrEven = isAlternateRow ? 0 : 1;
                const isPortrait = appDisplayMode.desktop
                    ? i % 2 === oddOrEven
                    : i % 2 === 0;
                return makeArticleTile(
                    item,
                    () => onTileClick(i),
                    {},
                    isPortrait,
                );
            } else return null;
        }) as React.ReactElement[]) ?? [];

    const handlePaginationPrefetch = (page: number | null) => {
        return trimPaginationURL(page, searchParams, location);
    };

    const isMobileAllTab = !currentCategory && mobile;

    if (
        (!tiles.length && !isMobileAllTab) ||
        (isMobileAllTab &&
            !groupedSearchResult?.Product.totalCount &&
            !groupedSearchResult?.Project.totalCount &&
            !groupedSearchResult?.Professional.totalCount &&
            !groupedSearchResult?.Blog.totalCount)
    ) {
        return (
            <Flex column className={css(S.NoResultWrapper)}>
                <Paragraph>{`Sorry, 0 ${ITEM_TYPE}s found.`}</Paragraph>
                <Paragraph>Try changing the filters or search term.</Paragraph>
            </Flex>
        );
    }

    const gridColumnTemplates = appDisplayMode.desktop
        ? desktopGridColumns(numberOfColumns)
        : numberOfColumns;

    return (
        <>
            <div className={css(S.TileGridWrapper)}>
                {!currentCategory && mobile ? (
                    <SearchResultCarousels
                        productTiles={
                            groupedSearchResult?.Product.hits?.map(
                                (result, i) => {
                                    const tile = makeProductTile(
                                        result,
                                        () => onTileClick(i),
                                        {
                                            size: 'dynamic',
                                            variables: {
                                                tilePadding: '0',
                                                dynamicSizingWidth: 158,
                                                dynamicSizingGapWidth: '0',
                                            },
                                        },
                                        false,
                                    );

                                    return tile;
                                },
                            ) ?? []
                        }
                        productCount={
                            groupedSearchResult?.Product.totalCount ?? 0
                        }
                        projectTiles={
                            groupedSearchResult?.Project.hits?.map(
                                (result, i) =>
                                    makeProjectTile(
                                        result,
                                        () => onTileClick(i),
                                        {
                                            size: 'dynamic',
                                            variables: {
                                                tilePadding: '0',
                                                dynamicSizingWidth: 240,
                                                dynamicSizingGapWidth: '0',
                                            },
                                        },
                                    ),
                            ) ?? []
                        }
                        projectCount={
                            groupedSearchResult?.Project.totalCount ?? 0
                        }
                        articleTiles={
                            groupedSearchResult?.Blog.hits?.map((result, i) =>
                                makeArticleTile(
                                    result,
                                    () => onTileClick(i),
                                    {},
                                    true,
                                ),
                            ) ?? []
                        }
                        articleCount={groupedSearchResult?.Blog.totalCount ?? 0}
                        professionalTiles={
                            groupedSearchResult?.Professional.hits.map(
                                (result, i) =>
                                    makeProfessionalTile(
                                        result,
                                        () => onTileClick(i),
                                        {
                                            size: 'dynamic',
                                            variables: {
                                                tilePadding: '0',
                                                dynamicSizingWidth: 240,
                                                dynamicSizingGapWidth: '0',
                                            },
                                        },
                                    ),
                            ) ?? []
                        }
                        professionalCount={
                            groupedSearchResult?.Professional.totalCount ?? 0
                        }
                    />
                ) : (
                    <>
                        <Grid
                            className={cx(css(S.GridWrapper), currentCategory)}
                            columns={gridColumnTemplates}
                        >
                            {tiles}
                        </Grid>
                        <Flex
                            className={css(S.PaginationWrapper)}
                            hAlign="center"
                            fill
                        >
                            {pagination && pagination.totalPages > 1 ? (
                                <Pagination
                                    prevButton={{
                                        as: Link,
                                        prefetch: 'intent',
                                        key: 'prevButton',
                                        to: handlePaginationPrefetch(
                                            pagination.prevPage,
                                        ),
                                    }}
                                    nextButton={{
                                        as: Link,
                                        prefetch: 'intent',
                                        key: 'nextButton',
                                        to: handlePaginationPrefetch(
                                            pagination.nextPage,
                                        ),
                                    }}
                                    renderItem={(item) => {
                                        return PaginationItem.create(
                                            {
                                                as: Link,
                                                prefetch: 'intent',
                                                key: item.type + item.page,
                                                to: handlePaginationPrefetch(
                                                    item.page,
                                                ),
                                                content: item,
                                            },
                                            { generateKey: true },
                                        );
                                    }}
                                    siblingCount={1}
                                    boundaryCount={1}
                                    count={pagination.totalPages}
                                    counter={{
                                        range: {
                                            from: pagination.currentRange.from,
                                            to: pagination.currentRange.to,
                                        },
                                        total: pagination.totalCount,
                                        textRenderer: (range, total) =>
                                            `${range.from} - ${range.to} of ${total} ${currentCategories}`,
                                    }}
                                    totalCount={pagination.totalCount}
                                    page={pagination.page}
                                    defaultPage={pagination.page}
                                    pageSize={pagination.pageSize}
                                    divider={null}
                                    appDisplayMode={appDisplayMode}
                                />
                            ) : null}
                        </Flex>
                    </>
                )}
                {pinErrorMessage && (
                    <MessageModal
                        title={'Warning'}
                        content={pinErrorMessage}
                        open={true}
                        onConfirm={() => setPinErrorMessage(null)}
                    />
                )}
            </div>
        </>
    );
};
